/*
 * Delivery promise.
 *
 * <delivery-promise> is rendered by snippets/delivery-promise.liquid with the
 * finished sentence for the moment the page was rendered, plus every other
 * sentence it could have been, keyed by [weekday][before/after cutoff].
 *
 * This exists because that render time is not the view time. Storefront HTML
 * sits in the CDN cache, and a page built at 22:55 can still be served at
 * 23:10 -- past the cutoff, promising a delivery that is no longer on offer.
 * So the element re-picks its line against the store's clock on load, and
 * again on the next cutoff, and the server rendered line is only the starting
 * point (and what a visitor without JavaScript keeps).
 *
 * No wording or delivery logic lives here on purpose: all 14 lines come from
 * Liquid, and this only answers "which day is it in the store's timezone, and
 * has today's cutoff passed".
 *
 * Second job, at the bottom: getting that same line into the Rebuy smart cart,
 * which the theme cannot render into.
 *
 * Loaded from layout/theme.liquid, only while the delivery promise is enabled.
 */

/* 1. The element ---------------------------------------------------------- */

if (!customElements.get('delivery-promise')) {
  class DeliveryPromise extends HTMLElement {
    connectedCallback() {
      try {
        this.messages = JSON.parse(this.dataset.messages);
        this.cutoffs = JSON.parse(this.dataset.cutoffs);
      } catch (error) {
        /* Keep the server rendered line rather than blanking the slot. */
        return;
      }

      this.timezone = this.dataset.timezone || 'Europe/Amsterdam';

      this.update();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    /*
     * The store's own wall clock, not the visitor's. A shopper in Lisbon is
     * still bound by an Amsterdam cutoff, so an hour's difference would hand
     * them the wrong promise for a full hour every evening.
     *
     * Intl is used rather than a fixed UTC offset baked into the markup so the
     * promise survives the DST switch on a page that is still cached.
     */
    storeNow() {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.timezone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(new Date());

      const value = (type) => (parts.find((part) => part.type === type) || {}).value;

      const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(value('weekday'));
      /* "24" is how en-GB spells midnight in hour12: false. */
      const hours = parseInt(value('hour'), 10) % 24;
      const minutes = parseInt(value('minute'), 10);

      return { weekday: weekday, minutes: hours * 60 + minutes };
    }

    update() {
      const now = this.storeNow();
      if (now.weekday < 0) return;

      const cutoff = this.cutoffs[now.weekday];
      /* -1 marks a day the fulfilment centre does not dispatch on at all. */
      const passed = cutoff < 0 || now.minutes >= cutoff;

      const line = (this.messages[now.weekday] || [])[passed ? 1 : 0];
      if (line && line !== this.textContent) this.textContent = line;

      this.scheduleNext(now, cutoff, passed);
    }

    /*
     * Re-check at the next moment the line can change: today's cutoff if it is
     * still ahead, otherwise midnight. Cheaper and steadier than polling, and
     * it covers the shopper who leaves a tab open across the cutoff.
     */
    scheduleNext(now, cutoff, passed) {
      const midnight = 24 * 60;
      const nextMinute = !passed && cutoff > now.minutes ? cutoff : midnight;
      /* A minute of slack so the tick lands after the boundary, not on it. */
      const delay = (nextMinute - now.minutes + 1) * 60 * 1000;

      clearTimeout(this.timer);
      this.timer = setTimeout(this.update.bind(this), delay);
    }
  }

  customElements.define('delivery-promise', DeliveryPromise);
}

/* 2. Cart drawer bridge --------------------------------------------------- */

/*
 * The theme's own drawer renders this row itself, from
 * sections/cart-drawer.liquid. The Rebuy smart cart builds its flyout in the
 * browser out of its own data, so the row has to be put there by hand -- from
 * the <template> layout/theme.liquid renders alongside it, which only exists
 * while the smart cart is the active cart.
 *
 * Unlike the sale mode messages, this line does not depend on what is in the
 * cart, so there is nothing to re-render: the same node is cloned in and left
 * to correct its own text.
 */
(function deliveryPromiseCart() {
  const template = document.querySelector('template[data-delivery-promise-cart]');
  /* Empty when the visitor is in a market the promise does not cover. */
  if (!template || !template.content.firstElementChild) return;

  let observing = false;
  let pending;

  function inject() {
    const list = document.querySelector('.rebuy-cart__flyout-items');
    /*
     * Idempotent: the observer below runs after every mutation of the flyout,
     * including the one this makes, and bails on a row that is already there
     * so nothing loops.
     */
    if (!list || list.querySelector('[data-delivery-promise-row]')) return;

    const row = template.content.firstElementChild.cloneNode(true);

    /*
     * Under the sale message when there is one, so the campaign keeps the top
     * of the cart. Whichever of the two lands first, the order comes out the
     * same -- sale mode prepends, and this goes after it.
     */
    const saleMessages = list.querySelector('[data-sale-mode-cart]');
    if (saleMessages) saleMessages.after(row);
    else list.prepend(row);
  }

  /*
   * Rebuy rebuilds the item list from scratch on quantity changes, which takes
   * the injected row with it, and the cart events do not all land after that
   * has happened. Watching the flyout puts the row back either way.
   */
  function observe() {
    const cart = document.getElementById('rebuy-cart');
    if (!cart || observing) return;

    observing = true;
    new MutationObserver(inject).observe(cart, { childList: true, subtree: true });
  }

  /* Several of these fire together on a single add, so the work is coalesced. */
  function refresh() {
    clearTimeout(pending);
    pending = setTimeout(() => {
      observe();
      inject();
    }, 150);
  }

  ['rebuy:smartcart.show', 'rebuy:cart.add', 'rebuy:cart.change', 'rebuy:cart.update'].forEach(
    (event) => document.addEventListener(event, refresh)
  );
})();
